import { createContext, use, useCallback, useMemo, useState } from 'react'

export interface TestCaseInjection {
  inputs: Record<string, string>
  expectedOutput: string
}

interface TestCaseInjectionContextValue {
  pendingInjection: TestCaseInjection | null
  injectTestCase: (injection: TestCaseInjection) => void
  clearInjection: () => void
}

const TestCaseInjectionContext =
  createContext<TestCaseInjectionContextValue | null>(null)

export function TestCaseInjectionProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [pendingInjection, setPendingInjection] =
    useState<TestCaseInjection | null>(null)

  const injectTestCase = useCallback((injection: TestCaseInjection) => {
    setPendingInjection(injection)
  }, [])

  const clearInjection = useCallback(() => {
    setPendingInjection(null)
  }, [])

  const value = useMemo<TestCaseInjectionContextValue>(
    () => ({ pendingInjection, injectTestCase, clearInjection }),
    [pendingInjection, injectTestCase, clearInjection],
  )

  return (
    <TestCaseInjectionContext value={value}>
      {children}
    </TestCaseInjectionContext>
  )
}

export function useTestCaseInjection(): TestCaseInjectionContextValue | null {
  return use(TestCaseInjectionContext)
}
